import React from 'react';

import { useCurrentUser } from 'Auth';

import { dateFNS } from '@gogame/intl-ts';
import { useScreenLink, useScreenState } from '@gogame/screens';

import { FR_PAGE } from 'scenes/FinancialResults/constants';
import { financialResultsLink } from 'scenes/FinancialResults/navigation';
import { IO_PAGE } from 'scenes/IO/constants';
import { ioLink } from 'scenes/IO/navigation';
import { REGS_STATS_PAGE } from 'scenes/RegsStats/constants';
import { POSTBACKS_REPORT_PAGE } from 'scenes/PostbacksReport/constants';
import { regsStatsLink } from 'scenes/RegsStats/navigation';
import { postbacksReportLink } from 'scenes/PostbacksReport/navigation';
import { trackerPresetLink } from 'scenes/Trackers/Tracker/navigation';
import {
  TRACKER_COHORT_PAGE,
  TRACKER_PAGE,
  TRACKER_CHART_PAGE,
} from 'scenes/Trackers/constants';

import { DEFAULT_PAY_TO, DEFAULT_PERIOD } from '../constants';
import {
  useLoadSubscription,
  useCreateSubscription,
  useUpdateSubscription,
  useIsUpdating,
} from '../data';
import SubscriptionsForm from './SubscriptionsForm';

const normalizeSubmittedData = (data) => {
  const { schedule } = data;

  const [runAtHours, runAtMinutes] = schedule.startTime?.split(':') || [];

  const date = schedule.runOnceDate
    ? new Date(schedule.runOnceDate)
    : new Date();

  return {
    ...data,
    schedule: {
      ...(schedule.repeatDays && { repeatDays: schedule.repeatDays }),
      period: schedule.period,
      startTime: new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        runAtHours,
        runAtMinutes
      ).toUTCString(),
    },
  };
};

export const numberWithZero = (i) => {
  return `${i.toString().padStart(2, '0')}`;
};
const getLink = (page) => {
  switch (page) {
    case TRACKER_PAGE:
    case TRACKER_COHORT_PAGE:
    case TRACKER_CHART_PAGE:
      return trackerPresetLink;
    case FR_PAGE:
      return financialResultsLink;
    case IO_PAGE:
      return ioLink;
    case REGS_STATS_PAGE:
      return regsStatsLink;
    case POSTBACKS_REPORT_PAGE:
      return postbacksReportLink;
    default:
      throw new Error('Link for subscribe not detected');
      return null;
  }
};

export const SubscriptionCreate = ({
  onClose,
  onExited,
  open,
  withPayTo = true,
  ...props
}) => {
  const [create] = useCreateSubscription();
  const isUpdating = useIsUpdating();
  const currentUser = useCurrentUser();

  const handleSubmit = async (values) => {
    await create(normalizeSubmittedData(values));
    onClose();
  };

  const offset = new Date().getMinutes() % 15;

  const nextTick = dateFNS.addMinutes(new Date(), 10 - offset);

  const hours = nextTick.getHours();
  const minutes = nextTick.getMinutes();

  const screenState = useScreenState();

  const to = useScreenLink({
    link: getLink(props.page),
    searchParams: screenState,
  });

  const initialValues = {
    schedule: {
      period: DEFAULT_PERIOD,
      startTime: `${numberWithZero(hours)}:${numberWithZero(minutes)}`,
    },
    recipients: [
      {
        id: currentUser.id,
        login: currentUser.login,
        name: currentUser.name,
        email: currentUser.userEmail,
      },
    ],
    payload: props.payload,
    page: props.page,
    queryString:
      to.pathname +
      `?${!to.search.includes('ready=true') ? 'ready=true&' : ''}` +
      to.search.slice(1),
  };

  if (withPayTo) {
    initialValues.payTo = DEFAULT_PAY_TO;
  }

  return (
    <SubscriptionsForm
      title={'Subscribe'}
      loading={isUpdating}
      onClose={onClose}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
      withPayTo={withPayTo}
    />
  );
};

const normalizeEditableData = (data) => {
  if (!data) {
    return {};
  }

  const {
    schedule: { startTime, period },
    payload,
    queryString,
    ...rest
  } = data;

  const runOnceDateUTC = new Date(startTime);
  const runOnceDateLocal = new Date(
    Date.UTC(
      runOnceDateUTC.getUTCFullYear(),
      runOnceDateUTC.getUTCMonth(),
      runOnceDateUTC.getUTCDate(),
      runOnceDateUTC.getUTCHours(),
      runOnceDateUTC.getUTCMinutes()
    )
  );

  const time = new Date(startTime);

  const hours = time.getHours();
  const minutes = time.getMinutes();

  return {
    ...rest,
    schedule: {
      ...data.schedule,
      runOnceDate: period === 1 ? runOnceDateLocal.toISOString() : undefined,
      startTime: `${hours}:${minutes}`,
    },
  };
};

export const SubscriptionEdit = ({ id, onClose, onExited, open }) => {
  const { data, isLoading } = useLoadSubscription(id);
  const [update, { isLoading: isUpdating }] = useUpdateSubscription();

  const handleSubmit = async (values) => {
    await update(normalizeSubmittedData(values));
    onClose();
  };

  const initialValues = normalizeEditableData(data);

  return (
    <SubscriptionsForm
      loading={isLoading || isUpdating}
      title={'Edit Subscription'}
      subTitle={initialValues.name}
      onClose={onClose}
      active={!!data}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
      isEdit={true}
    />
  );
};
